import {
  MobileDrawer,
  PlainButton,
  ColorPickerPopover,
  ColorPickerPopoverContent,
} from "@jugl-web/ui-kit";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { getFirstSymbol } from "@jugl-web/utils/utils/getFirstSymbol";
import { colord } from "colord";
import { FC, useMemo, useState } from "react";

interface BoardColorPickerProps {
  name: string;
  namePlaceholder: string;
  color: string;
  size: "md" | "lg";
  isReadonly?: boolean;
  onColorChange?: (color: string) => void;
}

const noop = () => {};

export const BoardColorPicker: FC<BoardColorPickerProps> = ({
  name,
  namePlaceholder,
  color,
  size,
  isReadonly,
  onColorChange = noop,
}) => {
  const [isMobileColorPickerOpen, setIsMobileColorPickerOpen] = useState(false);

  const { isMobile } = useAppVariant();
  const { t } = useTranslations();

  const colorPickerTextColor = useMemo(
    () =>
      colord(color).brightness() < 0.6
        ? "#ffffff"
        : colord(color).darken(0.7).toHex(),
    [color]
  );

  const boardNamePreviewSymbol = useMemo(
    () => getFirstSymbol(name.trim().length > 0 ? name : namePlaceholder),
    [name, namePlaceholder]
  );

  const commonClasses = cx(
    "font-secondary flex shrink-0 items-center justify-center uppercase transition",
    size === "md"
      ? "h-[56px] w-[56px] rounded-[10px] text-[22px]"
      : "h-[80px] w-[80px] rounded-[10px] text-[32px]",
    !isReadonly && "hover:brightness-90"
  );

  const commonStyles = {
    backgroundColor: color,
    color: colorPickerTextColor,
  };

  if (isMobile) {
    return (
      <>
        <PlainButton
          className={commonClasses}
          style={commonStyles}
          onClick={() => setIsMobileColorPickerOpen(true)}
        >
          {boardNamePreviewSymbol}
        </PlainButton>
        <MobileDrawer
          isOpen={isMobileColorPickerOpen}
          onClose={() => setIsMobileColorPickerOpen(false)}
        >
          <MobileDrawer.Content className="pt-5">
            <ColorPickerPopoverContent
              onClose={() => setIsMobileColorPickerOpen(false)}
              title={t({
                id: "tasks-board-dialog.icon-color",
                defaultMessage: "Icon color",
              })}
              color={color}
              onColorChange={onColorChange}
            />
          </MobileDrawer.Content>
        </MobileDrawer>
      </>
    );
  }

  return (
    <ColorPickerPopover
      title={t({
        id: "tasks-board-dialog.icon-color",
        defaultMessage: "Icon color",
      })}
      color={color}
      placement="right-start"
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger
          ref={triggerRef}
          as={PlainButton}
          disabled={isReadonly}
          className={commonClasses}
          style={commonStyles}
        >
          {boardNamePreviewSymbol}
        </Trigger>
      )}
      onColorChange={onColorChange}
    />
  );
};
